import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteWidget as deleteWidgetApi } from 'api/widgets';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { queryClient } from 'queries/queryClient';
import { widgetQueryKeys } from 'queries/widget-query-keys';

interface DeleteWidgetModalProps extends AlertDialog.AlertDialogRootProps {
    onDelete?: () => void;
}

interface UseDeleteWidgetMutationProps {
    widgetId: number;
    onSuccess?: () => void;
}
const useDeleteWidgetMutation = (props: UseDeleteWidgetMutationProps) => {
    const toast = useToast();
    const { onSuccess, widgetId } = props;
    const { accountId } = useCurrentAccount();

    return useMutation({
        mutationFn: () => deleteWidgetApi({ accountId, widgetId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            toast.success('Deleted widget successfully');
            onSuccess?.();
        },
        onError: () => {
            toast.error('Unable to delete widget');
        },
    });
};
const DeleteWidgetModal = (props: DeleteWidgetModalProps) => {
    const { onDelete, children, ...rest } = props;

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete configuration?</AlertDialog.Title>
                        <AlertDialog.Description>
                            This action is irreversible and will result in the deletion of all
                            settings related to the widget.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => onDelete?.()}>Delete</AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteWidgetModalTrigger = AlertDialog.Trigger;

const Root = DeleteWidgetModal;
const Trigger = DeleteWidgetModalTrigger;

export {
    DeleteWidgetModal,
    DeleteWidgetModalTrigger,
    //
    Root,
    Trigger,
    //
    useDeleteWidgetMutation,
};
